import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingBag, Trash2, Plus, Minus, ArrowLeft, ArrowRight, CreditCard, Lock, FileText } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CartPage = () => {
  const { items, removeItem, updateQuantity, getTotal, clearCart } = useCart();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [guestEmail, setGuestEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const handleCheckout = async () => {
    if (items.length === 0) {
      toast.error('Krepšelis tuščias');
      return;
    }

    // Check if user is logged in or has guest email
    if (!user && !guestEmail) {
      toast.error('Prašome įvesti el. pašto adresą');
      return;
    }

    setLoading(true);
    
    try {
      // Create order
      const orderData = {
        items: items.map(item => ({
          product_id: item.id,
          title: item.title,
          price: item.price,
          quantity: item.quantity
        })),
        total_amount: getTotal(),
        currency: 'EUR',
        guest_email: !user ? guestEmail : undefined
      };

      let orderResponse;
      
      if (user && token) {
        // Logged in user
        orderResponse = await axios.post(`${API_URL}/api/orders`, orderData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        // Guest checkout
        orderResponse = await axios.post(`${API_URL}/api/orders/guest`, orderData);
      }

      const order = orderResponse.data;

      // Initiate Paysera payment
      const paymentData = {
        order_id: order.order_id,
        total_amount: getTotal(),
        currency: 'EUR',
        email: user ? user.email : guestEmail
      };

      let paymentResponse;
      if (user && token) {
        paymentResponse = await axios.post(`${API_URL}/api/payments/initiate`, paymentData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        paymentResponse = await axios.post(`${API_URL}/api/payments/initiate/guest`, {
          ...paymentData,
          email: guestEmail
        });
      }

      // Redirect to Paysera payment
      if (paymentResponse.data.redirect_url) {
        clearCart();
        window.location.href = paymentResponse.data.redirect_url;
      }
      
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Klaida apdorojant užsakymą. Bandykite dar kartą.');
    } finally {
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-[#FAFAF8] py-16">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-4xl">
          <div className="bg-white rounded-3xl p-12 text-center border border-slate-100">
            <div className="w-20 h-20 bg-[#E8F5E9] rounded-full flex items-center justify-center mx-auto mb-6">
              <ShoppingBag className="w-10 h-10 text-[#4CAF50]" />
            </div>
            <h1 className="text-2xl font-['Poppins'] font-bold text-[#2D3748] mb-4">
              Krepšelis tuščias
            </h1>
            <p className="text-[#718096] mb-8">
              Pridėkite produktų į krepšelį, kad galėtumėte tęsti
            </p>
            <Link
              to="/parduotuve"
              className="inline-flex items-center bg-[#4CAF50] text-white hover:bg-[#43A047] rounded-full px-8 py-4 font-semibold transition-colors"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Grįžti į parduotuvę
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="cart-page" className="min-h-screen bg-[#FAFAF8]">
      {/* Header */}
      <section className="bg-white py-8 border-b border-slate-100">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-6xl">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-['Poppins'] font-bold text-[#2D3748]">
                Krepšelis
              </h1>
              <p className="text-[#718096]">{items.length} produktas(-ai)</p>
            </div>
            <Link
              to="/parduotuve"
              className="flex items-center text-[#4CAF50] hover:text-[#43A047] font-medium"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Tęsti apsipirkimą
            </Link>
          </div>
        </div>
      </section>

      {/* Cart Content */}
      <section className="py-12">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-4">
              {items.map((item) => (
                <div
                  key={item.id}
                  data-testid={`cart-item-${item.id}`}
                  className="bg-white rounded-2xl p-6 border border-slate-100 flex items-center gap-6"
                >
                  {/* Product Icon */}
                  <div className="w-16 h-16 bg-[#E8F5E9] rounded-xl flex items-center justify-center flex-shrink-0">
                    <FileText className="w-8 h-8 text-[#4CAF50]" />
                  </div>

                  {/* Product Info */}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-[#2D3748] mb-1">{item.title}</h3>
                    <p className="text-sm text-[#718096]">{item.file_type}</p>
                  </div>

                  {/* Quantity */}
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={() => updateQuantity(item.id, item.quantity - 1)}
                      className="w-8 h-8 rounded-full bg-[#FAFAF8] flex items-center justify-center hover:bg-[#E8F5E9] transition-colors"
                    >
                      <Minus className="w-4 h-4 text-[#718096]" />
                    </button>
                    <span className="w-8 text-center font-medium text-[#2D3748]">{item.quantity}</span>
                    <button
                      onClick={() => updateQuantity(item.id, item.quantity + 1)}
                      className="w-8 h-8 rounded-full bg-[#FAFAF8] flex items-center justify-center hover:bg-[#E8F5E9] transition-colors"
                    >
                      <Plus className="w-4 h-4 text-[#718096]" />
                    </button>
                  </div>

                  {/* Price */}
                  <div className="text-right">
                    <p className="font-bold text-[#2D3748]">{(item.price * item.quantity).toFixed(2)} €</p>
                    <p className="text-xs text-[#718096]">{item.price.toFixed(2)} € / vnt.</p>
                  </div>

                  {/* Remove */}
                  <button
                    onClick={() => removeItem(item.id)}
                    className="p-2 text-[#FF6B6B] hover:bg-[#FFEBEE] rounded-lg transition-colors"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              ))}
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-3xl p-6 border border-slate-100 sticky top-24">
                <h2 className="text-xl font-['Poppins'] font-semibold text-[#2D3748] mb-6">
                  Užsakymo suma
                </h2>

                <div className="space-y-3 mb-6">
                  <div className="flex justify-between text-[#718096]">
                    <span>Produktai ({items.length})</span>
                    <span>{getTotal().toFixed(2)} €</span>
                  </div>
                  <div className="flex justify-between text-[#718096]">
                    <span>Mokesčiai</span>
                    <span>0.00 €</span>
                  </div>
                  <hr className="border-slate-100" />
                  <div className="flex justify-between text-lg font-bold text-[#2D3748]">
                    <span>Iš viso</span>
                    <span>{getTotal().toFixed(2)} €</span>
                  </div>
                </div>

                {/* Guest Email */}
                {!user && (
                  <div className="mb-6">
                    <div className="bg-[#E8F5E9] rounded-xl p-4 mb-4">
                      <p className="text-sm font-medium text-[#4CAF50] mb-1">
                        ✓ Prisijungti nebūtina!
                      </p>
                      <p className="text-xs text-[#718096]">
                        Tiesiog įveskite el. paštą ir apmokėkite
                      </p>
                    </div>
                    <label className="block text-sm font-medium text-[#2D3748] mb-2">
                      El. paštas (produktų pristatymui)
                    </label>
                    <input
                      type="email"
                      value={guestEmail}
                      onChange={(e) => setGuestEmail(e.target.value)}
                      placeholder="jusu@pastas.lt"
                      data-testid="guest-email-input"
                      className="w-full h-12 rounded-xl border border-slate-200 px-4 focus:outline-none focus:border-[#4CAF50] focus:ring-2 focus:ring-[#4CAF50]/20"
                    />
                  </div>
                )}

                {/* Checkout Button */}
                <button
                  onClick={handleCheckout}
                  disabled={loading || (!user && !guestEmail)}
                  className="w-full bg-[#4CAF50] text-white hover:bg-[#43A047] rounded-full py-4 font-semibold transition-all flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5" />
                      <span>Mokėti su Paysera</span>
                    </>
                  )}
                </button>

                {/* Security Note */}
                <div className="flex items-center justify-center space-x-2 mt-4 text-sm text-[#718096]">
                  <Lock className="w-4 h-4" />
                  <span>Saugus mokėjimas</span>
                </div>

                {/* Login Prompt */}
                {!user && (
                  <div className="mt-6 p-4 bg-[#FFF3E0] rounded-xl">
                    <p className="text-sm text-[#718096] mb-2">
                      Turite paskyrą?
                    </p>
                    <Link
                      to="/prisijungti"
                      className="text-[#FF9F43] hover:text-[#E08A3A] font-medium text-sm"
                    >
                      Prisijunkite čia →
                    </Link>
                  </div>
                )}

                {/* Legal Links */}
                <div className="mt-6 pt-4 border-t border-slate-100">
                  <p className="text-xs text-[#718096] mb-2">Pirkdami sutinkate su:</p>
                  <div className="flex flex-wrap gap-2 text-xs">
                    <Link to="/salygos" className="text-[#4CAF50] hover:underline">Pirkimo taisyklėmis</Link>
                    <span className="text-slate-300">•</span>
                    <Link to="/privatumas" className="text-[#4CAF50] hover:underline">Privatumo politika</Link>
                  </div>
                  <div className="flex flex-wrap gap-2 text-xs mt-1">
                    <Link to="/pristatymas" className="text-[#4CAF50] hover:underline">Pristatymo sąlygos</Link>
                    <span className="text-slate-300">•</span>
                    <Link to="/grazinimas" className="text-[#4CAF50] hover:underline">Grąžinimo politika</Link>
                  </div>
                </div>
              </div>

              {/* Seller Info */}
              <div className="bg-white rounded-2xl p-4 border border-slate-100 mt-4">
                <p className="text-xs font-medium text-[#2D3748] mb-2">Pardavėjas</p>
                <p className="text-xs text-[#718096]">Dmitrij Borodin</p>
                <p className="text-xs text-[#718096]">Ind. veiklos pažymos nr. 706574</p>
                <p className="text-xs text-[#718096]">info@tavoemocijos.lt | +370 678 38321</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CartPage;
